import React, { useCallback, useEffect, useState } from "react";
import { Amplify } from "aws-amplify";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { USER_AUTH_CONFIG } from "../config/aws-config";
import CognitoSignIn from "../auth/cognito-sign-in";
import { AUTH_API } from "../config/config";
import { UserAuthCard, UserAuthShell } from "../auth/user-auth-layout";

async function accessToken() {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken;
  if (!token) throw new Error("No active user session");
  return token.toString();
}

async function oauthApi(path, options = {}) {
  const token = await accessToken();
  const response = await fetch(`${AUTH_API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!response.ok)
    throw new Error(`Authorization request failed (${response.status})`);
  return response.json();
}

export function buildApprovalRequest(
  spaceAccessMode,
  purpose,
  selectedSpaceIds,
  activeSpaceId
) {
  if (spaceAccessMode === "SELECTED") {
    return { authorizedScopeIds: selectedSpaceIds };
  }
  if (spaceAccessMode === "ALL") {
    return purpose === "mcp" ? { activeSpaceId } : {};
  }
  throw new Error("Unsupported Space access policy");
}

export function isApprovalReady(
  spaceAccessMode,
  purpose,
  selectedSpaceIds,
  activeSpaceId
) {
  if (spaceAccessMode === "SELECTED") return selectedSpaceIds.length > 0;
  if (spaceAccessMode === "ALL") {
    return purpose !== "mcp" || Boolean(activeSpaceId);
  }
  return false;
}

export default function OAuthAuthorize() {
  const transactionId = new URLSearchParams(window.location.search).get(
    "transaction_id"
  );
  const [transaction, setTransaction] = useState(null);
  const [selected, setSelected] = useState([]);
  const [activeSpaceId, setActiveSpaceId] = useState("");
  const [signedIn, setSignedIn] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const spaceAccessMode = transaction?.spaceAccessMode;
  const validSpaceAccessMode =
    spaceAccessMode === "ALL" || spaceAccessMode === "SELECTED";

  useEffect(() => {
    Amplify.configure(USER_AUTH_CONFIG);
  }, []);

  const load = useCallback(async () => {
    if (!transactionId) throw new Error("Missing authorization transaction");
    const data = await oauthApi(
      `/oauth/authorization-transactions/${transactionId}`
    );
    setTransaction(data);
    setSelected([]);
    setActiveSpaceId(
      data.initialActiveSpaceId || data.spaces?.[0]?.spaceId || ""
    );
    setSignedIn(true);
  }, [transactionId]);

  useEffect(() => {
    getCurrentUser()
      .then(() => {
        setSignedIn(true);
        return load().catch((loadError) => {
          setError(
            loadError?.message || "Unable to load this authorization request."
          );
        });
      })
      .catch(() => setSignedIn(false))
      .finally(() => setBusy(false));
  }, [load]);

  const decide = async (decision) => {
    setBusy(true);
    setError("");
    try {
      const data = await oauthApi(
        `/oauth/authorization-transactions/${transactionId}/${decision}`,
        {
          method: "POST",
          body:
            decision === "approve"
              ? JSON.stringify(
                  buildApprovalRequest(
                    spaceAccessMode,
                    transaction?.purpose,
                    selected,
                    activeSpaceId
                  )
                )
              : "{}",
        }
      );
      window.location.assign(data.redirectUri);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (busy && !transaction)
    return (
      <Centered>
        <UserAuthCard sx={{ maxWidth: 420, mx: "auto" }} contentSx={{ p: 4 }}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <CircularProgress size={30} />
            <Typography color="text.secondary">
              Loading authorization request…
            </Typography>
          </Stack>
        </UserAuthCard>
      </Centered>
    );

  if (!signedIn) {
    return (
      <Centered>
        <UserAuthCard
          sx={{ maxWidth: 440, mx: "auto" }}
          contentSx={{ p: { xs: 3, sm: 4 } }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <CognitoSignIn
            title="Sign in to mhome.ai"
            description="Continue to review an OAuth authorization request."
            onAuthenticated={load}
            allowFederated
            federatedReturnPath={`${window.location.pathname}${window.location.search}`}
          />
        </UserAuthCard>
      </Centered>
    );
  }

  return (
    <Centered>
      <UserAuthCard
        sx={{ maxWidth: 560, mx: "auto" }}
        contentSx={{ p: { xs: 3, sm: 4 } }}
      >
        <Stack spacing={2}>
          <Typography variant="h5">
            Authorize {transaction?.client?.clientName || "application"}
          </Typography>
          <Typography color="text.secondary">
            {transaction?.purpose === "mcp"
              ? "This Agent wants to use mhome.ai through Remote MCP."
              : "This application is requesting access to mhome.ai."}
          </Typography>
          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
            <Typography variant="body2">
              Client: {displayHost(transaction?.client?.clientId)}
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              Redirect: {transaction?.redirectUri}
            </Typography>
          </Box>
          {transaction?.loopbackRedirect && (
            <Alert severity="warning">
              This client will redirect to an application running on this
              device.
            </Alert>
          )}
          {spaceAccessMode === "ALL" ? (
            <>
              <Alert severity="warning">
                This application will be able to access every Space you are a
                member of, including Spaces you join in the future. Your role
                and each operation&apos;s normal permissions still apply.
              </Alert>
              {transaction?.purpose === "mcp" &&
                ((transaction?.spaces || []).length === 0 ? (
                  <Alert severity="error">
                    Create or join a Space before authorizing Remote MCP.
                  </Alert>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel id="oauth-active-space-label">
                      Initial active Space
                    </InputLabel>
                    <Select
                      labelId="oauth-active-space-label"
                      value={activeSpaceId}
                      label="Initial active Space"
                      onChange={(event) => setActiveSpaceId(event.target.value)}
                    >
                      {(transaction?.spaces || []).map((space) => (
                        <MenuItem key={space.spaceId} value={space.spaceId}>
                          {space.name || space.spaceId}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      This chooses the default Space for MCP operations. It does
                      not limit the authorization&apos;s all-Space access.
                    </Typography>
                  </FormControl>
                ))}
            </>
          ) : spaceAccessMode === "SELECTED" ? (
            <>
              <Typography variant="subtitle2">
                Choose the Spaces it may access
              </Typography>
              {(transaction?.spaces || []).map((space) => (
                <FormControlLabel
                  key={space.spaceId}
                  control={
                    <Checkbox
                      checked={selected.includes(space.spaceId)}
                      onChange={(event) =>
                        setSelected((old) =>
                          event.target.checked
                            ? [...old, space.spaceId]
                            : old.filter((id) => id !== space.spaceId)
                        )
                      }
                    />
                  }
                  label={space.name || space.spaceId}
                />
              ))}
            </>
          ) : (
            <Alert severity="error">
              This authorization request uses an unsupported Space access
              policy.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => decide("deny")} disabled={busy}>
              Deny
            </Button>
            <Button
              variant="contained"
              onClick={() => decide("approve")}
              disabled={
                busy ||
                !validSpaceAccessMode ||
                !isApprovalReady(
                  spaceAccessMode,
                  transaction?.purpose,
                  selected,
                  activeSpaceId
                )
              }
            >
              Authorize
            </Button>
          </Stack>
        </Stack>
      </UserAuthCard>
    </Centered>
  );
}

function displayHost(value) {
  try {
    return new URL(value).host;
  } catch (_) {
    return value || "Unknown client";
  }
}

function Centered({ children }) {
  return <UserAuthShell maxWidth={600}>{children}</UserAuthShell>;
}
